/**
 * FolderActionsMenu
 *
 * Kebab menu surfaced on a folder row in the SlideSense sidebar.
 * Admin-only — exposes Rename and Delete with confirmation. Reuses the
 * `updateFolder` / `deleteFolder` admin-api endpoints.
 */

import { useEffect, useRef, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { SlideSenseFolder } from "@/features/slide-sense/types"

import { Loader2, MoreVertical, Pencil, Trash2 } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { deleteFolder, updateFolder } from "@/features/slide-sense/admin-api"

export interface FolderActionsMenuProps {
  folder: SlideSenseFolder
  /** Called when the active folder was deleted so the parent can clear selection. */
  onDeleted?: (folderId: string) => void
}

export function FolderActionsMenu({ folder, onDeleted }: FolderActionsMenuProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [renameValue, setRenameValue] = useState(folder.name)
  const [isWorking, setIsWorking] = useState(false)
  const renameInputRef = useRef<HTMLInputElement>(null)

  // Focus the rename input once when the dialog opens. The previous
  // implementation used a ref callback that re-focused on every render —
  // which fought with the user's caret position as they typed or as
  // isWorking flipped.
  useEffect(() => {
    if (renameOpen) {
      // Defer to next tick so the Dialog has mounted the input.
      const id = window.setTimeout(() => renameInputRef.current?.focus(), 0)
      return () => window.clearTimeout(id)
    }
  }, [renameOpen])

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["slide-sense", "folders"] })

  const handleRename = async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === folder.name) {
      setRenameOpen(false)
      return
    }
    setIsWorking(true)
    try {
      await updateFolder(folder.id, { name: trimmed })
      toast.success(t("slideSense.renamedTo", { name: trimmed }))
      void invalidate()
      setRenameOpen(false)
    } catch (err) {
      console.error("Folder rename failed:", err)
      toast.error(t("slideSense.failedToRename"))
    } finally {
      setIsWorking(false)
    }
  }

  const handleDelete = async () => {
    setIsWorking(true)
    try {
      await deleteFolder(folder.id)
      toast.success(t("slideSense.folderDeleted", { name: folder.name }))
      void invalidate()
      setDeleteOpen(false)
      onDeleted?.(folder.id)
    } catch (err) {
      console.error("Folder delete failed:", err)
      toast.error(t("slideSense.failedToDeleteFolder"))
    } finally {
      setIsWorking(false)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            aria-label={t("slideSense.folderActions", { name: folder.name })}
            className="text-muted-foreground hover:text-foreground -m-0.5 flex size-6 shrink-0 items-center justify-center rounded-sm opacity-0 transition-opacity group-hover/menu-item:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
            type="button"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <MoreVertical className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
          <DropdownMenuItem
            onSelect={() => {
              setRenameValue(folder.name)
              setRenameOpen(true)
            }}
          >
            <Pencil className="mr-2 h-3.5 w-3.5" />
            {t("slideSense.renameFolder")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={() => setDeleteOpen(true)}
          >
            <Trash2 className="mr-2 h-3.5 w-3.5" />
            {t("slideSense.deleteFolder")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("slideSense.renameFolderTitle")}</DialogTitle>
            <DialogDescription>{t("slideSense.renameFolderPrompt")}</DialogDescription>
          </DialogHeader>
          <Input
            ref={renameInputRef}
            data-1p-ignore
            maxLength={100}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleRename()
            }}
          />
          <DialogFooter>
            <Button
              disabled={isWorking}
              size="sm"
              variant="outline"
              onClick={() => setRenameOpen(false)}
            >
              {t("slideSense.cancel")}
            </Button>
            <Button
              disabled={isWorking || !renameValue.trim() || renameValue.trim() === folder.name}
              size="sm"
              onClick={() => void handleRename()}
            >
              {isWorking && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {t("slideSense.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("slideSense.deleteFolderTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("slideSense.deleteFolderConfirm", { name: folder.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isWorking}>{t("slideSense.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              disabled={isWorking}
              onClick={(e) => {
                e.preventDefault()
                void handleDelete()
              }}
            >
              {isWorking && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {t("slideSense.deleteFolder")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
